import { useContext } from 'react';
import { ModalContext } from './ModalProvider';
import type { ModalContextValue, ModalConfig } from './types';
import { MODAL_CONFIGS, getModalDefaultProps } from './registry';
import type { ModalType } from './registry';

/**
 * Hook to consume the ModalContext
 * Provides convenient access to modal management functions
 *
 * @returns ModalContextValue - Contains all modal management functions
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, isModalOpen } = useModal();
 *
 * // Open a modal
 * const modalId = openModal({
 *   type: 'CostsAndFees',
 *   priority: 2,
 *   onClose: () => console.log('Modal closed')
 * });
 *
 * // Check if modal is open
 * if (isModalOpen('CostsAndFees')) {
 *   console.log('CostsAndFees modal is open');
 * }
 *
 * // Close the modal
 * closeModal(modalId);
 * ```
 */
export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);

  if (!context) {
    // Return a safe default during SSR
    return {
      activeModals: [],
      openModal: () => '',
      closeModal: () => {},
      closeAllModals: () => {},
      closeModalByType: () => {},
      isModalOpen: () => false,
      getModalById: () => undefined,
    };
  }

  return context;
};

// Type helper for modal opener function - simplified
type ModalOpenerFunction = (props?: Omit<ModalConfig, 'id' | 'type'>) => string;

// Auto-generate modal opener functions from config
type ModalOpeners = {
  [K in ModalType as `open${Capitalize<K>}Modal`]: ModalOpenerFunction;
};

/**
 * Type-safe modal opener hook with auto-generated methods
 * @returns Function to open modals with type safety and default props applied
 */
export const useModalOpener = (): ModalOpeners => {
  const { openModal } = useModal();

  // Auto-generate opener methods from modal config
  const openers = {} as ModalOpeners;

  (Object.keys(MODAL_CONFIGS) as ModalType[]).forEach((modalType) => {
    const methodName = `open${modalType.charAt(0).toUpperCase() + modalType.slice(1)}Modal` as keyof ModalOpeners;

    openers[methodName] = ((props: Omit<ModalConfig, 'id' | 'type'> = {}) => {
      const defaultProps = getModalDefaultProps(modalType);

      // Merge props correctly - defaultProps are style props that should be merged with component props
      const mergedConfig: Omit<ModalConfig, 'id'> = {
        ...props,
        type: modalType,
        props: {
          ...defaultProps, // Default style props (size, padding, etc.)
          ...props.props, // Component-specific props from caller
        },
      };

      return openModal(mergedConfig);
    }) as ModalOpenerFunction;
  });

  return openers;
};

/**
 * Hook to check if a specific modal type is open
 * @param type - The modal type to check
 * @returns Boolean indicating if the modal is open
 */
export const useIsModalOpen = (type: ModalType): boolean => {
  const { isModalOpen } = useModal();
  return isModalOpen(type);
};

/**
 * Hook to get active modals count
 * @returns Number of active modals
 */
export const useActiveModalsCount = (): number => {
  const { activeModals } = useModal();
  return activeModals.length;
};

export default useModal;
